
export default function generateCrosstab(
  rows,
  rowKey,
  colKey,
  rowVarMeta = {},
  colVarMeta = {}
) {
  if (
    (!rowKey && !Array.isArray(rowKey)) ||
    (!colKey && !Array.isArray(colKey)) ||
    !Array.isArray(rows) ||
    rows.length === 0
  ) {
    return { error: "Invalid inputs", table: [] };
  }

  /* ------------------------------
     NORMALIZE VALUE
  -------------------------------*/
  const normKey = (v) => {
    if (v === null || v === undefined) return "__MISSING__";
    const s = String(v).trim();
    const cleaned = s.replace(/^0+([1-9])/, "$1").replace(/\.0+$/, "");
    return cleaned === "" ? "__MISSING__" : cleaned;
  };

  /* ------------------------------
     GRID LABEL EXTRACTOR
  -------------------------------*/
  const extractGridLabel = (title) => {
    if (!title) return "";
    const m = title.match(/^[A-Za-z0-9_]+r\d+(.*)$/);
    return m ? m[1].trim() : title;
  };

  /* ------------------------------
     LABEL MAP BUILDER
  -------------------------------*/
  const buildLabelMap = (meta) => {
    const map = {};
    if (!meta) return map;

    if (meta.valueLabels) {
      for (const [code, label] of Object.entries(meta.valueLabels)) {
        map[normKey(code)] = label;
      }
    }

    if (Array.isArray(meta.values)) {
      meta.values.forEach(v => {
        const code = normKey(v.index ?? v.value ?? v.Id);
        map[code] = v.title ?? v.label ?? v.Name ?? code;
      });
    }

    if (Array.isArray(meta.Rows)) {
      meta.Rows.forEach(r => {
        const code = normKey(r.index ?? r.value ?? r.Id);
        map[code] = r.title ?? r.label ?? code;
      });
    }

    return map;
  };

  /* ------------------------------
     KEYS & METAS
  -------------------------------*/
  const rowKeys = Array.isArray(rowKey) ? rowKey : [rowKey];
  const colKeys = Array.isArray(colKey) ? colKey : [colKey];

  const rowMetas = Array.isArray(rowVarMeta)
    ? rowVarMeta
    : rowKeys.map(() => rowVarMeta || {});

  const colMetas = Array.isArray(colVarMeta)
    ? colVarMeta
    : colKeys.map(() => colVarMeta || {});

  const rowLabelMaps = rowMetas.map(m => buildLabelMap(m));
  const colLabelMaps = colMetas.map(m => buildLabelMap(m));

  /* ------------------------------
     GRID COLUMN DETECTION
  -------------------------------*/
  const detectGridColumns = (key) => {
    const allCols = Object.keys(rows[0] || {});
    const m = String(key).match(/^(.*?r)\d+$/);
    const prefix = m
      ? m[1]
      : String(key).includes("r")
        ? String(key).split("r")[0] + "r"
        : String(key);

    return allCols.filter(col =>
      col.toLowerCase().startsWith(prefix.toLowerCase())
    );
  };

  const firstRowKey = rowKeys[0];
  const firstColKey = colKeys[0];

  const firstRowMeta = rowMetas[0] || {};
  const firstColMeta = colMetas[0] || {};

  /* ------------------------------
     GRID FLAGS (FIXED ORDER ✅)
  -------------------------------*/
  const isRowGrid = !!(
    firstRowMeta &&
    (
      firstRowMeta.type === "MultiResponse" ||
      (Array.isArray(firstRowMeta.Rows) && firstRowMeta.Rows.length > 1)
    )
  );

  const isColGrid = !!(
    firstColMeta &&
    (
      firstColMeta.type === "MultiResponse" ||
      (Array.isArray(firstColMeta.Rows) && firstColMeta.Rows.length > 1)
    )
  );

  const rowGrid = isRowGrid ? detectGridColumns(firstRowKey) : [];
  const colGrid = isColGrid ? detectGridColumns(firstColKey) : [];

  /* ------------------------------
     MULTIGRID SHORT-CIRCUIT
  -------------------------------*/
  if (isRowGrid || isColGrid) {
    const multi = buildMultiGridCrosstab(
      rows,
      rowGrid,
      colGrid,
      firstRowMeta,
      firstColMeta,
      extractGridLabel
    );

    return {
      ...multi,
      rowLeaves: rowGrid.map(c =>
        deriveLabelForReturn(c, firstRowMeta, extractGridLabel)
      ),
      colLeaves: colGrid.map(c =>
        deriveLabelForReturn(c, firstColMeta, extractGridLabel)
      )
    };
  }

  /* ------------------------------
     SINGLE LEVEL (NO NESTING)
  -------------------------------*/
  if (rowKeys.length === 1 && colKeys.length === 1) {
    const rk = rowKeys[0], ck = colKeys[0];
    const rowLabelMap = rowLabelMaps[0] || {};
    const colLabelMap = colLabelMaps[0] || {};

    const rowValues = [...new Set(rows.map(r => normKey(r[rk])))];
    const colValues = [...new Set(rows.map(r => normKey(r[ck])))];

    const table = [];

    rowValues.forEach(rCode => {
      const rowLabel = rowLabelMap[rCode] ?? rCode;
      const rowBase = rows.filter(r => normKey(r[rk]) === rCode).length;

      const rowObj = { rowLabel };

      colValues.forEach(cCode => {
        const colLabel = colLabelMap[cCode] ?? cCode;
        const count = rows.filter(r =>
          normKey(r[rk]) === rCode &&
          normKey(r[ck]) === cCode
        ).length;

        rowObj[colLabel] = {
          value: count,
          base: rowBase,
          percent: rowBase ? +(count * 100 / rowBase).toFixed(1) : 0
        };
      });

      table.push(rowObj);
    });

    return {
      rowValues: rowValues.map(v => rowLabelMap[v] ?? v),
      colValues: colValues.map(v => colLabelMap[v] ?? v),
      table,
      rowLeaves: rowValues.map(v => rowLabelMap[v] ?? v),
      colLeaves: colValues.map(v => colLabelMap[v] ?? v)
    };
  }

  /* ------------------------------
     NESTED KEYS (UNCHANGED)
  -------------------------------*/
  const getLabelForLevel = (level, value, isRow = true) => {
    const maps = isRow ? rowLabelMaps : colLabelMaps;
    return maps[level]?.[normKey(value)] ?? String(value ?? "__MISSING__");
  };

  const buildLeafCombinations = (keys) => {
    const combos = new Map();
    rows.forEach(r => {
      const seq = keys.map(k => normKey(r[k]));
      const key = seq.join("|||");
      if (!combos.has(key)) combos.set(key, seq);
    });
    return [...combos.values()];
  };

  const buildLeafSeqs = (keys, isRow) => {
    const combos = buildLeafCombinations(keys);
    return {
      tree: [],
      leafSeqs: combos.map(seq => ({
        seq,
        label: seq.map((v, i) => getLabelForLevel(i, v, isRow)).join(" > ")
      }))
    };
  };

  const rowBuilt = buildLeafSeqs(rowKeys, true);
  const colBuilt = buildLeafSeqs(colKeys, false);

  const table = [];

  rowBuilt.leafSeqs.forEach(rLeaf => {
    const rowBase = rows.filter(r =>
      rowKeys.every((k, i) => normKey(r[k]) === rLeaf.seq[i])
    ).length;

    const rowObj = { rowLabel: rLeaf.label };

    colBuilt.leafSeqs.forEach(cLeaf => {
      const count = rows.filter(r =>
        rowKeys.every((k, i) => normKey(r[k]) === rLeaf.seq[i]) &&
        colKeys.every((k, i) => normKey(r[k]) === cLeaf.seq[i])
      ).length;

      rowObj[cLeaf.label] = {
        value: count,
        base: rowBase,
        percent: rowBase ? +(count * 100 / rowBase).toFixed(1) : 0
      };
    });

    table.push(rowObj);
  });

  return {
    rowValues: rowBuilt.leafSeqs.map(r => r.label),
    colValues: colBuilt.leafSeqs.map(c => c.label),
    table,
    rowLeaves: rowBuilt.leafSeqs.map(r =>
      getLabelForLevel(r.seq.length - 1, r.seq.at(-1), true)
    ),
    colLeaves: colBuilt.leafSeqs.map(c =>
      getLabelForLevel(c.seq.length - 1, c.seq.at(-1), false)
    )
  };

  /* ------------------------------
     HELPER
  -------------------------------*/
  function deriveLabelForReturn(col, meta, extractGridLabel) {
    if (!meta) return col;
    const rMatch = col.match(/r(\d+)/i);
    const rIndex = rMatch ? Number(rMatch[1]) : null;
    if (Array.isArray(meta.Rows) && rIndex !== null) {
      const found = meta.Rows.find(r => String(r.index) === String(rIndex));
      if (found) return extractGridLabel(found.title);
    }
    if (meta.valueLabels) {
      if (meta.valueLabels[col] !== undefined) return meta.valueLabels[col];
      if (meta.valueLabels[Number(col)] !== undefined) return meta.valueLabels[Number(col)];
    }
    if (Array.isArray(meta.values)) {
      const found = meta.values.find(v => String(v.index) === String(col));
      if (found) return found.title ?? found.label ?? found.Name ?? col;
    }
    return col;
  }
}

/* =====================================================
   MULTIGRID BUILDER (UNCHANGED)
=====================================================*/
function buildMultiGridCrosstab(
  rows,
  rowCols,
  colCols,
  rowMeta,
  colMeta,
  extractGridLabel
) {
  const deriveLabel = (col, meta) => {
    const m = col.match(/r(\d+)/i);
    if (m && Array.isArray(meta?.Rows)) {
      const found = meta.Rows.find(r => String(r.index) === m[1]);
      if (found) return extractGridLabel(found.title);
    }
    return col;
  };

  const rowLabels = rowCols.map(c => deriveLabel(c, rowMeta));
  const colLabels = colCols.map(c => deriveLabel(c, colMeta));

  const base = rows.filter(r =>
    rowCols.some(col => r[col] == 1)
  ).length;

  const table = [];

  rowCols.forEach((rCol, i) => {
    const rowObj = { rowLabel: rowLabels[i] };

    colCols.forEach((cCol, j) => {
      const count = rows.filter(r => r[rCol] == 1 && r[cCol] == 1).length;
      rowObj[colLabels[j]] = {
        value: count,
        base,
        percent: base ? +(count * 100 / base).toFixed(1) : 0
      };
    });

    table.push(rowObj);
  });

  return {
    rowValues: rowLabels,
    colValues: colLabels,
    base,
    table
  };
}

